// ─── Planning des repas : config + menus-types (grille hebdo) ─────────
// Deux briques, doc-id = espaceId (My Meals) — côté MoPCal le même modèle
// existe avec doc-id = calendrierId ; les règles Firestore acceptent les deux
// (cf. `accesCalOuEspace`) :
//   • repasConfig/{espaceId}            : moments de repas configurables.
//   • repasMenus/{espaceId}/menus/{id}  : menus-types hebdo, cases
//       { '<jourIdx>_<momentId>': {recetteId} | {repasId,personnes} | {libre} }
//       jourIdx 0=Lundi…6=Dimanche.
// Porté de MoPCal-v3 services/repas.js — sans les Semaines / l'application au
// calendrier / la liste de courses (incrément suivant).
import { db } from '@mop/core';
import { collection, doc, onSnapshot, setDoc, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';

export const JOURS_SEMAINE = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export const MOMENTS_DEFAUT = [
  { id: 'dej',   label: 'Déjeuner' },
  { id: 'diner', label: 'Dîner' },
];

export function cleCaseRepas(jourIdx, momentId) {
  return `${jourIdx}_${momentId}`;
}

// Normalise une heure saisie ("8:00", "08h00", "8h", "8") vers le format "8h00".
export function normaliserHeureRepas(s) {
  if (!s) return '';
  const m = String(s).trim().match(/^(\d{1,2})\s*[h:.]?\s*(\d{0,2})$/);
  if (!m) return '';
  const h = parseInt(m[1], 10);
  if (isNaN(h) || h > 23) return '';
  const mm = (m[2] || '0').padStart(2, '0');
  if (parseInt(mm, 10) > 59) return '';
  return `${h}h${mm}`;
}

// ── Config (moments) ────────────────────────────────────────────────
export function ecouterRepasConfig(espaceId, callback) {
  if (!espaceId) { callback(null); return () => {}; }
  return onSnapshot(doc(db, 'repasConfig', espaceId),
    s => callback(s.exists() ? s.data() : null),
    () => callback(null));
}
export async function enregistrerRepasConfig(espaceId, config) {
  await setDoc(doc(db, 'repasConfig', espaceId), { ...config, maj: new Date().toISOString() }, { merge: true });
}
export function momentsDe(config) {
  return (config && Array.isArray(config.moments) && config.moments.length) ? config.moments : MOMENTS_DEFAUT;
}
// Les plages de repas se paramètrent AU NIVEAU DU MENU. `config.moments`
// n'est qu'un repli pour les menus créés avant cette évolution.
export function momentsDuMenu(menu, config) {
  return (menu && Array.isArray(menu.moments) && menu.moments.length) ? menu.moments : momentsDe(config);
}

// ── Catégories de recette (bibliothèque) ────────────────────────────
export const CATEGORIES_RECETTE_DEFAUT = [
  { id: 'matin',     label: 'Petit-déjeuner',     emoji: '🥐' },
  { id: 'midi',      label: 'Déjeuner',           emoji: '🍽️' },
  { id: 'soir',      label: 'Dîner',              emoji: '🌙' },
  { id: 'rapide',    label: 'Rapide en semaine',  emoji: '⚡' },
  { id: 'collation', label: 'Collation',          emoji: '🍎' },
  { id: 'dessert',   label: 'Dessert',            emoji: '🍰' },
];
const SANS_CATEGORIE = { id: '', label: 'Sans catégorie', emoji: '📄' };

export function categoriesRecetteDe(config) {
  const c = config && config.categoriesRecette;
  return (Array.isArray(c) && c.length) ? c : CATEGORIES_RECETTE_DEFAUT;
}
export function categorieRecetteDe(id, cats) {
  return (cats || CATEGORIES_RECETTE_DEFAUT).find(c => c.id === id) || SANS_CATEGORIE;
}
// Index de tri : les catégories inconnues (et « sans catégorie ») passent en fin.
export function ordreCategorieRecette(id, cats) {
  const i = (cats || CATEGORIES_RECETTE_DEFAUT).findIndex(c => c.id === id);
  return i < 0 ? 999 : i;
}

// ── Menus-types (grilles hebdo) ─────────────────────────────────────
function menusRef(espaceId) { return collection(db, 'repasMenus', espaceId, 'menus'); }

export function ecouterMenus(espaceId, callback, onErreur) {
  if (!espaceId) { callback([]); return () => {}; }
  return onSnapshot(menusRef(espaceId), (snap) => {
    const liste = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    liste.sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
    callback(liste);
  }, (e) => { callback([]); onErreur?.(e); });
}

export async function creerMenu(espaceId, { nom = 'Menu', cases = {}, moments = [], actif = true } = {}) {
  if (!espaceId) throw new Error('Aucun espace actif');
  const ref = await addDoc(menusRef(espaceId), { nom, cases, moments, actif, maj: new Date().toISOString() });
  return ref.id;
}
export async function enregistrerMenu(espaceId, menuId, champs) {
  await updateDoc(doc(db, 'repasMenus', espaceId, 'menus', menuId), { ...champs, maj: new Date().toISOString() });
}
export async function supprimerMenu(espaceId, menuId) {
  await deleteDoc(doc(db, 'repasMenus', espaceId, 'menus', menuId));
}
export async function dupliquerMenu(espaceId, menu) {
  return creerMenu(espaceId, { nom: `${menu.nom} (copie)`, cases: menu.cases, moments: menu.moments, actif: true });
}

// ── Cases de la grille (pures — pas d'accès Firestore) ──────────────
export function affecterRepasCase(cases, jourIdx, momentId, repasId, personnes) {
  return { ...cases, [cleCaseRepas(jourIdx, momentId)]: { repasId, ...(personnes ? { personnes } : {}) } };
}
export function affecterRecetteCase(cases, jourIdx, momentId, recetteId, personnes) {
  return { ...cases, [cleCaseRepas(jourIdx, momentId)]: { recetteId, ...(personnes ? { personnes } : {}) } };
}
export function affecterLibreCase(cases, jourIdx, momentId, libre) {
  return { ...cases, [cleCaseRepas(jourIdx, momentId)]: { libre } };
}
export function viderCase(cases, jourIdx, momentId) {
  const next = { ...cases };
  delete next[cleCaseRepas(jourIdx, momentId)];
  return next;
}
